// Shopify Storefront access for the catalog.
//
// The old shopify-buy 2.11 SDK issues GraphQL that current Shopify rejects
// (the Checkout API was removed), so we talk to the Storefront API directly and
// send purchases to Shopify's hosted cart/checkout via cart permalinks. This
// keeps checkout on Shopify exactly as before, with no fragile SDK dependency.

export const SHOP_DOMAIN = 'newfangle-llc.myshopify.com';
const STOREFRONT_TOKEN = 'a756723c50787179031f4335cd6feda4';
const API_VERSION = '2024-01';

const ENDPOINT = `https://${SHOP_DOMAIN}/api/${API_VERSION}/graphql.json`;

const PRODUCTS_QUERY = `{
  products(first: 50) {
    edges {
      node {
        id
        title
        handle
        productType
        tags
        availableForSale
        featuredImage { url }
        options { name values }
        variants(first: 50) {
          edges {
            node {
              id
              title
              availableForSale
              price { amount currencyCode }
              image { url }
              selectedOptions { name value }
            }
          }
        }
      }
    }
  }
}`;

// A Shopify default single-variant product carries one option named "Title"
// with the value "Default Title" — not a real choice, so we hide it.
function realOptions(options) {
	return (options || []).filter(
		(o) => !(o.name === 'Title' && o.values.length === 1 && o.values[0] === 'Default Title')
	);
}

// Fetch and normalize the catalog into a flat, view-friendly shape.
export function fetchProducts() {
	return fetch(ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
		},
		body: JSON.stringify({ query: PRODUCTS_QUERY }),
	})
		.then((res) => res.json())
		.then((json) => {
			if (!json.data || !json.data.products) {
				throw new Error('Storefront API returned no products');
			}
			return json.data.products.edges.map((edge) => {
				const n = edge.node;
				const variants = n.variants.edges.map((e) => ({
					id: e.node.id,
					title: e.node.title,
					available: e.node.availableForSale,
					price: e.node.price ? e.node.price.amount : null,
					currency: e.node.price ? e.node.price.currencyCode : 'USD',
					image: e.node.image ? e.node.image.url : null,
					options: e.node.selectedOptions || [], // [{ name, value }]
				}));
				const first = variants[0] || {};
				return {
					id: n.id,
					title: n.title,
					handle: n.handle,
					productType: n.productType || '',
					tags: n.tags || [],
					available: n.availableForSale,
					image: n.featuredImage ? n.featuredImage.url : null,
					options: realOptions(n.options), // [{ name, values }]
					variants,
					// convenience defaults (first variant) used by single-variant books
					variantId: first.id || null,
					price: first.price || null,
					currency: first.currency || 'USD',
				};
			});
		});
}

// -------------------- Shopify Cart API (single source of truth) --------------
//
// The bag is a real Shopify cart, not a local snapshot. We persist only the cart
// ID; line items, prices, the subtotal, and the checkoutUrl are always read back
// from Shopify, so display never drifts and Shopify owns lifecycle/clearing:
// after checkout completes the cart is done and cart(id) returns null, at which
// point we drop the stored id and start fresh.

function graphql(query, variables) {
	return fetch(ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
		},
		body: JSON.stringify({ query, variables }),
	}).then((res) => res.json());
}

const CART_FIELDS = `
	id
	checkoutUrl
	totalQuantity
	cost { subtotalAmount { amount currencyCode } }
	lines(first: 100) {
		edges {
			node {
				id
				quantity
				merchandise {
					... on ProductVariant {
						id
						title
						image { url }
						price { amount currencyCode }
						product { title featuredImage { url } }
					}
				}
			}
		}
	}
`;

function normalizeCart(cart) {
	if (!cart) return null;
	return {
		id: cart.id,
		checkoutUrl: cart.checkoutUrl,
		totalQuantity: cart.totalQuantity,
		subtotal: cart.cost && cart.cost.subtotalAmount ? cart.cost.subtotalAmount.amount : '0',
		currency: cart.cost && cart.cost.subtotalAmount ? cart.cost.subtotalAmount.currencyCode : 'USD',
		lines: (cart.lines ? cart.lines.edges : []).map((edge) => {
			const n = edge.node;
			const m = n.merchandise || {};
			const p = m.product || {};
			const variantTitle = m.title && m.title !== 'Default Title' ? m.title : '';
			return {
				lineId: n.id,
				variantId: m.id,
				qty: n.quantity,
				title: p.title || m.title || '',
				variantTitle,
				price: m.price ? m.price.amount : null,
				image: (m.image && m.image.url) || (p.featuredImage && p.featuredImage.url) || null,
			};
		}),
	};
}

// Returns the normalized cart, or null if it no longer exists (expired or the
// checkout completed) — the caller should then forget the stored id.
export function cartFetch(cartId) {
	return graphql(`query cart($id: ID!) { cart(id: $id) { ${CART_FIELDS} } }`, { id: cartId }).then(
		(j) => normalizeCart(j.data && j.data.cart)
	);
}

export function cartCreate(lines) {
	return graphql(
		`mutation cartCreate($lines: [CartLineInput!]!) {
			cartCreate(input: { lines: $lines }) { cart { ${CART_FIELDS} } userErrors { message } }
		}`,
		{ lines }
	).then((j) => normalizeCart(j.data && j.data.cartCreate && j.data.cartCreate.cart));
}

export function cartLinesAdd(cartId, lines) {
	return graphql(
		`mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
			cartLinesAdd(cartId: $cartId, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } }
		}`,
		{ cartId, lines }
	).then((j) => normalizeCart(j.data && j.data.cartLinesAdd && j.data.cartLinesAdd.cart));
}

export function cartLinesUpdate(cartId, lines) {
	return graphql(
		`mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
			cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } }
		}`,
		{ cartId, lines }
	).then((j) => normalizeCart(j.data && j.data.cartLinesUpdate && j.data.cartLinesUpdate.cart));
}

export function cartLinesRemove(cartId, lineIds) {
	return graphql(
		`mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
			cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { ${CART_FIELDS} } userErrors { message } }
		}`,
		{ cartId, lineIds }
	).then((j) => normalizeCart(j.data && j.data.cartLinesRemove && j.data.cartLinesRemove.cart));
}
